/**
 * UFS 客户端工厂
 *
 * 从 Vite 环境变量构造 UnifiedFileServiceClient。
 * 认证策略：
 * - 生产模式（VITE_REMOTE_USER_ID 未设）：浏览器不带任何 auth 头，依赖 api-gateway 注入 X-User-ID；
 *   fetch 带 credentials: 'include' 携带 session cookie。
 * - 开发模式（VITE_REMOTE_USER_ID=dev-user）：用 extraHeaders 塞 X-User-ID 模拟网关注入。
 */

import { UnifiedFileServiceClient } from '@ufs/client'
import type { UfsSyncConfig } from './types'

/**
 * 从环境变量读取 UFS 配置
 * 返回 null 表示未配置 UFS（前端保持纯本地模式）
 */
export function readUfsConfigFromEnv(): UfsSyncConfig | null {
  const baseUrl = import.meta.env.VITE_UFS_BASE_URL as string | undefined
  if (!baseUrl) {
    // 兼容期：也认旧的 VITE_REMOTE_API_URL
    const legacyUrl = import.meta.env.VITE_REMOTE_API_URL as string | undefined
    if (!legacyUrl) return null
    return buildConfig(legacyUrl)
  }
  return buildConfig(baseUrl)
}

function buildConfig(baseUrl: string): UfsSyncConfig {
  const devUserId = import.meta.env.VITE_REMOTE_USER_ID as string | undefined
  const config: UfsSyncConfig = { baseUrl }

  if (devUserId) {
    // 开发模式：模拟网关注入 X-User-ID
    config.extraHeaders = { 'X-User-ID': devUserId }
  } else {
    // 生产模式：依赖 session cookie，由 api-gateway 注入身份
    config.credentials = 'include'
  }

  return config
}

/**
 * 创建 UFS 客户端单例
 * 多次调用返回同一实例，避免重复构造
 */
let clientInstance: UnifiedFileServiceClient | null = null

export function getUfsClient(): UnifiedFileServiceClient {
  if (clientInstance) return clientInstance

  const config = readUfsConfigFromEnv()
  if (!config) {
    throw new Error(
      'UFS 未配置：请设置 VITE_UFS_BASE_URL 环境变量指向 unified-file-service（经 api-gateway 代理）'
    )
  }

  clientInstance = new UnifiedFileServiceClient({
    baseUrl: config.baseUrl,
    credentials: config.credentials,
    extraHeaders: config.extraHeaders,
  })
  return clientInstance
}

/**
 * 检查 UFS 是否已配置（用于决定是否启用同步）
 */
export function isUfsConfigured(): boolean {
  return readUfsConfigFromEnv() !== null
}
